import { useTheme } from '@mui/material/styles';
import { createFormHook } from '@tanstack/react-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import type {
  DtoV1EnumContactCategoryType,
  MicrosoftAspNetCoreMvcProblemDetails as ProblemDetails,
} from '~/__generated__/api';
import Box from '~/common/components/Box';
import Button from '~/common/components/Buttons/Button';
import FormLabel from '~/common/components/FormLabel';
import Input from '~/common/components/Inputs/Input';
import Oops from '~/common/components/Oops';
import { useSnackbar } from '~/common/components/Snackbar/snackbarStore';
import Spinner from '~/common/components/Spinner';
import Text from '~/common/components/Texts/Text';
import toastLang from '~/common/constants/language.json';
import {
  fieldContext,
  formContext,
  useFieldContext,
} from '~/common/form/formContexts';
import useUrl from '~/common/hooks/useUrl';
import Divider from '~/common/mui/Divider';
import Combobox, { type Option } from '~/common/mui/Inputs/Selects/Combobox';
import MUIDialog from '~/common/mui/MUIDialog/MUIDialog';
import { typedApiClient } from '~/common/util/api-client';
import { useModalState } from '~/modals/modalStore';

import { getNameForContact } from './utils';

const ENTITY_NAME_MAP = {
  srUnderwriter: 'SeniorUnderwriter',
  underwriter2: 'UnderwriterII',
  underwritingTeam: 'UnderwritingTeam',
  collateralUnderwriter: 'CollateralUnderwriter',
} as const;

const { useAppForm } = createFormHook({
  fieldContext,
  formContext,
  fieldComponents: {
    Combobox: ComboboxFormField,
  },
  formComponents: {},
});

export function LoanContactsModalContainer() {
  const { isOpen } = useModalState('loanContacts');

  if (!isOpen) {
    return null;
  }

  return <LoanContactsModal />;
}

type LoanContactsFormData = {
  contacts: {
    srUnderwriter: Option;
    underwriter2: Option;
    underwritingTeam: Option;
    collateralUnderwriter: Option;
  };
  queueComment: string;
};

const loanContactsDataQueryKey = ['loanContacts'];

function LoanContactsModal() {
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const { isOpen, onClose } = useModalState('loanContacts');
  const { obfuscatedLoanIdentifier } = useUrl();

  const {
    data: loanContactsData,
    isLoading: isLoanContactsLoading,
    isError: isLoanContactsError,
  } = useQuery({
    queryKey: loanContactsDataQueryKey,
    queryFn: async () => {
      const { data } =
        await typedApiClient.v1.internalLoanContactsAndQueueComment(
          obfuscatedLoanIdentifier.obfuscatedLenderDatabaseId,
          obfuscatedLoanIdentifier.obfuscatedLoanRecordId,
          {
            contactCategories: [
              'AccountExecutive',
              'ProjectAssistant',
              'CollateralUnderwriter',
              'SeniorUnderwriter',
              'UnderwritingTeam',
              'UnderwriterII',
            ],
          },
        );

      return data;
    },
    select: (data) => {
      function getCurrentContactOption(
        contactCategoryID: DtoV1EnumContactCategoryType,
      ) {
        const contact = data.contactOptions.find(
          (c) => c.contactCategoryID === contactCategoryID,
        )?.currentContact;

        if (contact === undefined) {
          return;
        }

        return {
          label: getNameForContact(contact),
          id: contact?.contactID,
        };
      }

      function getSelectOptionsForContactCategory(
        contactCategoryID: DtoV1EnumContactCategoryType,
      ) {
        return (
          data.contactOptions
            .find((c) => c.contactCategoryID === contactCategoryID)
            ?.options?.map((contact) => ({
              id: contact.contactID,
              label: getNameForContact(contact),
              saveData: {
                obfuscatedLoanIdentifier,
                entityId: contact.entityID,
                contactId: contact.contactID,
                type: contactCategoryID,
              },
            })) ?? []
        );
      }

      const currentAccountExecutiveContactOption =
        getCurrentContactOption('AccountExecutive');
      const currentProjectAssistantContactOption =
        getCurrentContactOption('ProjectAssistant');
      const currentSrUnderwriterContactOption =
        getCurrentContactOption('SeniorUnderwriter');
      const currentUnderwriter2ContactOption =
        getCurrentContactOption('UnderwriterII');
      const currentUnderwritingTeamContactOption =
        getCurrentContactOption('UnderwritingTeam');
      const currentCollateralUnderwriterContactOption = getCurrentContactOption(
        'CollateralUnderwriter',
      );

      return {
        screenData: {
          contacts: {
            accountExecutive: currentAccountExecutiveContactOption?.label,
            projectAssistant: currentProjectAssistantContactOption?.label,
          },
          comboBoxOptions: {
            srUnderwriter:
              getSelectOptionsForContactCategory('SeniorUnderwriter'),
            underwriter2: getSelectOptionsForContactCategory('UnderwriterII'),
            underwritingTeam:
              getSelectOptionsForContactCategory('UnderwritingTeam'),
            collateralUnderwriter: getSelectOptionsForContactCategory(
              'CollateralUnderwriter',
            ),
          },
        },
        formData: {
          contacts: {
            srUnderwriter: currentSrUnderwriterContactOption,
            underwriter2: currentUnderwriter2ContactOption,
            underwritingTeam: currentUnderwritingTeamContactOption,
            collateralUnderwriter: currentCollateralUnderwriterContactOption,
          },
          queueComment: data.queueComment ?? null,
        } satisfies LoanContactsFormData,
      };
    },
  });

  const { mutate, isLoading: isSaving } = useMutation({
    mutationFn: async (formData: LoanContactsFormData) => {
      function getContactForCategory(
        contactCategory: keyof typeof formData.contacts,
      ) {
        const contact = formData.contacts[contactCategory]?.id;

        const screenDataContact =
          loanContactsData?.screenData?.comboBoxOptions?.[
            contactCategory
          ]?.find((c) => c.id === contact);

        return (
          screenDataContact?.saveData ?? {
            type: ENTITY_NAME_MAP[contactCategory],
            contactId: null,
            entityId: null,
            obfuscatedLoanIdentifier,
          }
        );
      }

      await typedApiClient.v1.underwriterAssignments(
        obfuscatedLoanIdentifier.obfuscatedLenderDatabaseId,
        obfuscatedLoanIdentifier.obfuscatedLoanRecordId,
        {
          contacts: [
            getContactForCategory('srUnderwriter'),
            getContactForCategory('collateralUnderwriter'),
            getContactForCategory('underwriter2'),
            getContactForCategory('underwritingTeam'),
          ].filter((c) => c !== null),
          queueCommentData: {
            obfuscatedLoanIdentifiers: [obfuscatedLoanIdentifier],
            queueComment: formData.queueComment ?? null,
          },
        },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries(loanContactsDataQueryKey);
      onClose();
      showSnackbar({
        severity: 'success',
        message: 'Contacts updated successfully',
        duration: 1500,
      });
    },
    onError({ response }: AxiosError<ProblemDetails>) {
      showSnackbar({
        severity: 'error',
        message:
          response?.data?.title ||
          toastLang.TOAST_ERROR_DESCRIPTION_UNDERWRITER,
        duration: 5000,
      });
    },
  });

  const form = useAppForm({
    defaultValues: loanContactsData?.formData,
    onSubmit: ({ value }) => {
      mutate(value);
    },
  });

  const isModalLoading = isLoanContactsLoading || !loanContactsData;

  return (
    <MUIDialog
      title="Contacts"
      open={isOpen}
      onClose={onClose}
      anchorSelector='[data-modal-anchor="loanContacts"]'
      dialogPosition="left"
      paperSx={{
        minWidth: '40.5rem',
        minHeight: '27.5rem',
      }}
      primaryButton={{
        name: 'Update Assignments',
        isSubmitting: isSaving,
        isDisabled: isSaving || isLoanContactsLoading,
        onSubmit: () => {
          form.handleSubmit();
        },
      }}
    >
      {isLoanContactsError && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem',
            h: '362px',
          }}
        >
          <Oops />
          <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <Button variant="solid" onClick={onClose}>
              Close Contacts
            </Button>
            <Button
              colorScheme="blue"
              sx={{
                backgroundColor: theme.palette.blue[600],
              }}
              onClick={() => {
                queryClient.resetQueries(loanContactsDataQueryKey);
              }}
            >
              Try Again
            </Button>
          </Box>
        </Box>
      )}
      {isModalLoading && !isLoanContactsError && (
        <Box>
          <Spinner backgroundColor="transparent" height="362px" />
        </Box>
      )}
      {!isLoanContactsError && !isModalLoading && (
        <Box sx={{ paddingTop: '1.5rem', px: 0 }}>
          <Box
            sx={{
              display: 'flex',
            }}
          >
            <Box flexBasis="50%" mr="0.5rem">
              <TextValueDisplay
                label="Account Executive"
                value={
                  loanContactsData?.screenData?.contacts?.accountExecutive ??
                  'N/A'
                }
              />
            </Box>
            <Box>
              <TextValueDisplay
                label="Project Assistant"
                value={loanContactsData?.screenData?.contacts?.projectAssistant}
              />
            </Box>
          </Box>
          <Divider
            sx={{
              my: '1rem',
            }}
          />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '1rem',
            }}
          >
            <form.AppField name="contacts.srUnderwriter">
              {(field) => (
                <field.Combobox
                  label="SR Underwriter"
                  options={
                    loanContactsData?.screenData?.comboBoxOptions?.srUnderwriter
                  }
                />
              )}
            </form.AppField>
            <form.AppField name="contacts.underwriter2">
              {(field) => (
                <field.Combobox
                  label="Underwriter II"
                  options={
                    loanContactsData?.screenData?.comboBoxOptions?.underwriter2
                  }
                />
              )}
            </form.AppField>
            <form.AppField name="contacts.underwritingTeam">
              {(field) => (
                <field.Combobox
                  label="Underwriting Team"
                  options={
                    loanContactsData?.screenData?.comboBoxOptions
                      ?.underwritingTeam
                  }
                />
              )}
            </form.AppField>
            <form.AppField name="contacts.collateralUnderwriter">
              {(field) => (
                <field.Combobox
                  label="Collateral Underwriter"
                  options={
                    loanContactsData?.screenData?.comboBoxOptions
                      ?.collateralUnderwriter
                  }
                />
              )}
            </form.AppField>
          </Box>
          <Divider
            sx={{
              my: '1rem',
            }}
          />
          <form.Field name="queueComment">
            {(field) => (
              <>
                <FormLabel
                  label="Queue Comment"
                  htmlFor={field.name}
                  variant="small"
                />
                <Input
                  type="text"
                  id={field.name}
                  isInvalid={!field.state.meta.isValid}
                  onChange={(e) => field.handleChange(e.currentTarget.value)}
                  onBlur={() => field.handleBlur()}
                  value={field.state.value ?? ''}
                />
              </>
            )}
          </form.Field>
        </Box>
      )}
    </MUIDialog>
  );
}

type ComboboxFormFieldProps = {
  initialFocus?: boolean;
  label: string;
  options: Option[];
};

function ComboboxFormField(props: Readonly<ComboboxFormFieldProps>) {
  const field = useFieldContext<Option>();

  return (
    <Box>
      <FormLabel htmlFor={field.name} label={props.label} variant="small" />
      <Combobox
        name={field.name}
        options={props.options}
        error={!field.state.meta.isValid}
        value={field.state.value}
        onChange={(option) => {
          field.setValue(option);
        }}
        onBlur={() => field.handleBlur()}
      />
    </Box>
  );
}

type TextValueDisplayProps = {
  label: string;
  value: string;
};

function TextValueDisplay({ label, value }: Readonly<TextValueDisplayProps>) {
  return (
    <Box>
      <Text
        id={label}
        sx={{
          fontWeight: 400,
          lineHeight: '1rem',
          fontSize: '0.875rem',
        }}
      >
        {label}
      </Text>
      <Text
        aria-labelledby={label}
        sx={{
          fontWeight: 500,
          lineHeight: '1.5rem',
        }}
      >
        {value}
      </Text>
    </Box>
  );
}
